import _CircularProgress from "@mui/material/CircularProgress";
import _TextField from "@mui/material/TextField";
import _DialogActions from "@mui/material/DialogActions";
import _DialogContent from "@mui/material/DialogContent";
import _Typography from "@mui/material/Typography";
import _DialogTitle from "@mui/material/DialogTitle";
import _Box from "@mui/material/Box";
import _Tooltip from "@mui/material/Tooltip";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _Dialog2 from "@mui/material/Dialog";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import validator from 'validator';
import * as Yup from 'yup';
import { EmployeeService, GeogroupService } from '../services';
import { Button as CustomButton } from './button';
export function CommunityJoinDialog({
  Button: _Button,
  Dialog: _Dialog,
  currentUser,
  geogroup,
  handleSubmit: _handleSubmit,
  onClose,
  ...props
}) {
  const [hasCode, setHasCode] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    isValid,
    isSubmitting,
    values,
    setValues,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      code: '',
      professionalEmail: ''
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string(),
      lastName: Yup.string(),
      professionalEmail: Yup.string().test(value => !value || validator.isEmail(value)),
      code: Yup.string()
    }),
    validateOnMount: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit
  });
  useEffect(() => {
    if (props.open) {
      setHasCode(!currentUser);
      setEmailSent(false);
      setValues({
        firstName: '',
        lastName: '',
        code: '',
        professionalEmail: currentUser?.email || ''
      });
    }
  }, [props.open]);
  async function onSubmit({
    firstName,
    lastName,
    code,
    professionalEmail
  }, {
    setSubmitting
  }) {
    if (geogroup) {
      setSubmitting(true);
      _handleSubmit?.();
      try {
        if (geogroup.type === 'city' || geogroup.openMembership) {
          await GeogroupService.joinGeogroup(geogroup.id);
          onClose(geogroup);
        } else if (geogroup.type === 'company') {
          if (hasCode) {
            if (geogroup.partner) {
              if (!geogroup.partner.whiteListedDomains || geogroup.partner.whiteListedDomains.length === 0) {
                if (currentUser) {
                  await EmployeeService.createEmployee(geogroup.partner, {
                    professionalEmail: currentUser.email,
                    invitationCode: code,
                    userId: currentUser.id
                  });
                  onClose(geogroup);
                } else {
                  try {
                    await EmployeeService.createEmployee(geogroup.partner, {
                      invitationCode: code
                    });
                  } catch (err) {
                    if (typeof err === 'object' && err && 'status' in err && err.status === 403) {
                      enqueueSnackbar(t('commons.communities.join_dialog.server_error_code'), {
                        variant: 'error'
                      });
                      setSubmitting(false);
                      return;
                    }
                  }
                  onClose(geogroup, {
                    invitationCode: code
                  });
                }
              } else {
                await EmployeeService.createEmployee(geogroup.partner, {
                  professionalEmail,
                  invitationCode: code
                });
                setEmailSent(true);
              }
            } else {
              throw new Error('geogroup has no partner');
            }
          } else {
            await GeogroupService.joinGeogroup(geogroup.id, {
              disabledRetrieval: true,
              firstName,
              lastName,
              professionalEmail
            });
            onClose(geogroup, {
              partner: geogroup.partner ? {
                code: geogroup.partner.code,
                title: geogroup.partner.title,
                icon: geogroup.partner.icon
              } : null,
              waiting: true
            });
          }
        } else {
          await GeogroupService.joinGeogroupWithInvitation(code, {
            geogroupId: geogroup.id
          });
          onClose(geogroup);
        }
      } catch (err) {
        if (typeof err === 'object' && err && 'status' in err && typeof err.status === 'number' && [403, 409, 412].includes(err.status)) {
          if (err.status === 403) {
            enqueueSnackbar(t('commons.communities.join_dialog.server_error_whitelist', {
              geogroupTitle: geogroup.title
            }), {
              variant: 'error'
            });
          } else if (err.status === 409) {
            enqueueSnackbar(t('commons.communities.join_dialog.server_error_already_joined'), {
              variant: 'error'
            });
          } else {
            setEmailSent(true);
          }
        } else {
          console.error(err);
          enqueueSnackbar(t('commons.communities.join_dialog.server_error_email'), {
            variant: 'error'
          });
        }
      }
      setSubmitting(false);
    }
  }
  if (!geogroup) return /*#__PURE__*/React.createElement(React.Fragment, null);
  const Dialog = _Dialog || _Dialog2;
  const Button = _Button || CustomButton;
  return /*#__PURE__*/React.createElement(Dialog, _extends({
    fullWidth: true,
    disableEscapeKeyDown: isSubmitting,
    maxWidth: "xs",
    onClose: (_, reason) => (!isSubmitting || reason !== 'backdropClick') && onClose()
  }, props), /*#__PURE__*/React.createElement(_Box, {
    position: "absolute",
    right: 12,
    top: 12
  }, /*#__PURE__*/React.createElement(_Tooltip, {
    title: t('commons.actions.close')
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(_IconButton, {
    disabled: isSubmitting,
    onClick: () => onClose()
  }, /*#__PURE__*/React.createElement(_Close, null))))), /*#__PURE__*/React.createElement(_DialogTitle, {
    sx: {
      paddingRight: 8
    }
  }, t('commons.communities.join_dialog.title', {
    context: geogroup.type === 'company' && !hasCode ? 'ask' : geogroup.type !== 'city' && !geogroup.openMembership ? 'code' : '',
    geogroupTitle: geogroup.title
  })), emailSent ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_DialogContent, null, /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary"
  }, t('commons.communities.join_dialog.email_sent')))), /*#__PURE__*/React.createElement(_DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    onClick: () => onClose(),
    size: "medium",
    variant: "contained"
  }, t('commons.actions.close')))) : /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(_DialogContent, null, geogroup.type === 'company' && !hasCode ? /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 4
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "body2"
  }, t('commons.communities.join_dialog.description_company')), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    required: true,
    disabled: isSubmitting,
    id: "firstName",
    label: t('commons.first_name'),
    name: "firstName",
    onChange: handleChange,
    size: "small",
    slotProps: {
      inputLabel: {
        shrink: true
      }
    },
    value: values.firstName,
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    required: true,
    disabled: isSubmitting,
    id: "lastName",
    label: t('commons.last_name'),
    name: "lastName",
    onChange: handleChange,
    size: "small",
    slotProps: {
      inputLabel: {
        shrink: true
      }
    },
    value: values.lastName,
    variant: "outlined"
  }), geogroup.partner && /*#__PURE__*/React.createElement(_TextField, {
    required: true,
    disabled: isSubmitting,
    helperText: t('commons.professional_email_helper_restriction', {
      partnerTitle: geogroup.partner.title
    }),
    id: "professionalEmail",
    label: t('commons.email_professional'),
    margin: "none",
    name: "professionalEmail",
    onChange: handleChange,
    size: "small",
    slotProps: {
      inputLabel: {
        shrink: true
      }
    },
    type: "email",
    value: values.professionalEmail,
    variant: "outlined"
  }))) : /*#__PURE__*/React.createElement(React.Fragment, null, geogroup.type !== 'city' && !geogroup.openMembership ? /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 4
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "body2"
  }, t('commons.communities.join_dialog.description_code', {
    context: geogroup.type === 'company' ? geogroup.partner && geogroup.partner.whiteListedDomains && geogroup.partner.whiteListedDomains.length > 0 ? 'email' : 'company' : ''
  })), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    required: true,
    disabled: isSubmitting,
    id: "code",
    label: t('commons.code'),
    name: "code",
    onChange: handleChange,
    size: "small",
    slotProps: {
      inputLabel: {
        shrink: true
      }
    },
    value: values.code,
    variant: "outlined"
  }), geogroup.type === 'company' && geogroup.partner && geogroup.partner.whiteListedDomains && geogroup.partner.whiteListedDomains.length > 0 && /*#__PURE__*/React.createElement(_TextField, {
    required: true,
    disabled: isSubmitting,
    helperText: t('commons.professional_email_helper_restriction', {
      partnerTitle: geogroup.partner.title
    }),
    id: "professionalEmail",
    label: t('commons.email_professional'),
    margin: "none",
    name: "professionalEmail",
    onChange: handleChange,
    size: "small",
    slotProps: {
      inputLabel: {
        shrink: true
      }
    },
    type: "email",
    value: values.professionalEmail,
    variant: "outlined"
  }))) : /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    variant: "body2"
  }, t('commons.communities.join_dialog.description', {
    username: currentUser?.username || ''
  })))), /*#__PURE__*/React.createElement(_DialogActions, null, currentUser && geogroup.type === 'company' && /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: isSubmitting,
    onClick: () => {
      setValues({
        firstName: '',
        lastName: '',
        code: '',
        professionalEmail: ''
      });
      setHasCode(!hasCode);
    },
    size: "medium",
    type: "reset",
    variant: "text"
  }, t(hasCode ? 'commons.communities.join_dialog.actions.has_no_code' : 'commons.communities.join_dialog.actions.has_code')), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: isSubmitting || !isValid || (geogroup.type === 'company' ? hasCode ? geogroup.partner?.whiteListedDomains && geogroup.partner.whiteListedDomains.length > 0 && !values.professionalEmail || !values.code : !values.firstName || !values.lastName : geogroup.type !== 'city' && !geogroup.openMembership && !values.code),
    size: "medium",
    startIcon: isSubmitting && /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16
    }),
    type: "submit",
    variant: "contained"
  }, t(geogroup.type === 'company' && !hasCode ? 'commons.communities.join_dialog.actions.ask' : 'commons.communities.join_dialog.actions.join')))));
}